/** Authored Hero Forge reference port from fable-rogue 03a219c0
 * tools/character-builder/src/. Numerical keyframes/curves are preserved.
 * See PROVENANCE.md for the explicit skeletal adapter boundary. */
/**
 * The forge's super attacks, played as a game plays its cinematics. Each is authored on a story clock that real time
 * runs through with hit-stops (the action held still on a big blow while the camera shakes) and slow motion, and comes
 * with its own camera: cuts, fly-rounds and close-ups in the hero's character space (ClipEvents.camera), and with
 * flashes, impact frames, speed lines and a world drained of colour for the screen (ClipEvents.screen). Each time round
 * runs the other way, from where the last one ended.
 */
import { type Quat, type V3, clamp, hash3, mix, normalize, qAxis, qEuler, qFromTo, qMul, qRotate, qSlerp } from './math';
import { B, type Pose, resetPose } from './rig';
import { DUMMY_MIDDLE } from './props';
import { type ClipContext, type ClipEvents, HOLDS, aimAlong, blow, hipsHeight, noEvents, rot, secondary } from './animation';

// ---------------------------------------------------------------------------
// what a super attack tells the stage
// ---------------------------------------------------------------------------

/** The training dummy a super attack strikes (props.ts), where the clip throws it. */
export interface Target {
  /** Its middle (character space) and how it is turned about it. */
  at: V3;
  turn: Quat;
  /** 1 as built, 0 gone. */
  size: number;
  /** How wildly its arms flail, 0..1. */
  flail: number;
  /** How far it has come apart along the cut through it: 0 whole, a quarter its top slid off the cut, 1 both halves flung away. */
  split: number;
}

/** A blow landing, for the effects: when (clip time), where (character space) and along which way its cut runs. */
export interface Hit {
  time: number;
  at: V3;
  along: V3;
  size: number;
  /** How long its slash hangs in the air (s; 0 a blow's own quick one), and how thick it is (1 a blow's). */
  linger: number;
  width: number;
  /** A burst of light; dust thrown up off the floor; a shock wave over the floor. */
  burst: boolean;
  dust: boolean;
  ring: boolean;
  /** Straw knocked out of the dummy, 0..1. */
  straw: number;
  /** Every slash still hanging in the air flares up and is gone. */
  flare: boolean;
}

/**
 * A camera shot in the hero's character space: where the eye is, what it looks at, its field of view and roll
 * (radians), how much of the view it takes over from the viewer's own (0..1) and how hard it shakes (0..1).
 */
export interface Shot { eye: V3; target: V3; fov: number; roll: number; weight: number; shake: number }

/**
 * What the screen does over the picture, each 0..1: a white flash, an impact frame (the picture in inverted ink), the
 * colour drained, speed lines, and the world dimmed round the action so its light stands out.
 */
export interface Screen { flash: number; impact: number; mono: number; speed: number; dim: number }

// ---------------------------------------------------------------------------
// the story clock
// ---------------------------------------------------------------------------

interface Segment { r: number; s: number; rate: number }
interface Clock { segments: Segment[]; length: number }

/**
 * A story clock: `story` seconds of action, held still for each of `stops` (story time, real seconds held) and slowed
 * over each of `slows` (story from and to, the rate it runs at there), as real time runs through it.
 */
function clock(story: number, stops: [number, number][], slows: [number, number, number][]): Clock {
  const marks = [...new Set([0, story, ...stops.map(([s]) => s), ...slows.flatMap(([a, b]) => [a, b])])].sort((a, b) => a - b);
  const rateAt = (s: number) => slows.find(([a, b]) => s >= a && s < b)?.[2] ?? 1;
  const segments: Segment[] = [];
  let r = 0;
  marks.forEach((s, i) => {
    for (const [at, hold] of stops) if (at === s) { segments.push({ r, s, rate: 0 }); r += hold; }
    if (i + 1 >= marks.length) return;
    const rate = rateAt(s);
    segments.push({ r, s, rate });
    r += (marks[i + 1] - s) / rate;
  });
  return { segments, length: r };
}

/** The story's time `r` real seconds into it. */
function storyAt(c: Clock, r: number): number {
  let seg = c.segments[0];
  for (const x of c.segments) {
    if (x.r > r) break;
    seg = x;
  }
  return seg.s + (r - seg.r) * seg.rate;
}

/** The real time the story reaches `s`: a moment it stands still on, as it begins to. */
function realAt(c: Clock, s: number): number {
  for (let i = 0; i < c.segments.length; i++) {
    const seg = c.segments[i], end = c.segments[i + 1]?.s ?? Infinity;
    if (seg.rate > 0 && s <= end) return seg.r + (s - seg.s) / seg.rate;
  }
  return c.length;
}

// ---------------------------------------------------------------------------
// timing
// ---------------------------------------------------------------------------

/**
 * How a value goes from one key to the next: eased in and out, snapped (most of the way at once, then settling), as a
 * thrown thing rises (`arc`) and falls (`late`), straight, or past the mark and back.
 */
type Curve = 'ease' | 'snap' | 'arc' | 'late' | 'linear' | 'back';
function shape(curve: Curve, x: number) {
  switch (curve) {
    case 'ease': return x * x * (3 - 2 * x);
    case 'snap': return 1 - (1 - x) ** 3;
    case 'arc': return 1 - (1 - x) ** 2;
    case 'late': return x * x;
    case 'linear': return x;
    case 'back': { const y = x - 1; return 1 + 2.7 * y * y * y + 1.7 * y * y; }
  }
}

/** A value through keys (time, value, how it gets there from the key before). */
type Key = [number, number, Curve?];
function keyed(u: number, keys: Key[]): number {
  if (u <= keys[0][0]) return keys[0][1];
  for (let i = 0; i + 1 < keys.length; i++) {
    const [t0, v0] = keys[i], [t1, v1, curve = 'ease'] = keys[i + 1];
    if (u <= t1) return v0 + (v1 - v0) * shape(curve, (u - t0) / (t1 - t0));
  }
  return keys[keys.length - 1][1];
}

/** Several values at once through keys, as `keyed`. */
type Keys<T extends readonly number[]> = [number, T, Curve?][];
function keyedAll<T extends readonly number[]>(u: number, keys: Keys<T>): number[] {
  if (u <= keys[0][0]) return [...keys[0][1]];
  for (let i = 0; i + 1 < keys.length; i++) {
    const [t0, a] = keys[i], [t1, b, curve = 'ease'] = keys[i + 1];
    if (u <= t1) {
      const k = shape(curve, (u - t0) / (t1 - t0));
      return a.map((v, j) => v + (b[j] - v) * k);
    }
  }
  return [...keys[keys.length - 1][1]];
}

const ease = (x: number) => shape('ease', clamp(x, 0, 1));
/** A step from 0 to 1 over `a`..`b`, eased. */
const ramp = (u: number, a: number, b: number) => ease((u - a) / (b - a));
/** Within `a`..`b`. */
const during = (u: number, a: number, b: number) => u > a && u < b;
/** A pulse that peaks a moment after `at` and dies away: a jolt when struck. */
const jolt = (u: number, at: number, width = 0.06) => (u > at ? ((u - at) / width) * Math.exp(1 - (u - at) / width) : 0);

// ---------------------------------------------------------------------------
// the body
// ---------------------------------------------------------------------------

/** The hero turned `yaw` about the vertical and leant forward (+) or back. */
const facing = (yaw: number, lean = 0): Quat => qMul(qEuler(0, yaw, 0), qEuler(lean, 0, 0));
/**
 * Facing the viewer, its head turned `beta` round from straight up (towards the way the run comes from, as the viewer
 * sees it turn anticlockwise on a run to the right), in the plane the star is cut in; leant forward or back on that.
 */
const inPlane = (dir: number, beta: number, lean = 0): Quat => qMul(qAxis([0, 0, 1], dir * beta), qEuler(lean, 0, 0));

/** The legs, each thigh's turn (about X, Y and Z), the shin's bend and the foot's tip, left leg then right. */
type Legs = readonly [number, number, number, number, number, number, number, number, number, number];
const LEGS = {
  ready: [-0.3, 0.3, 0.2, 0.5, 0, 0.12, -0.3, -0.2, 0.35, 0],
  crouch: [-0.75, 0.25, 0.18, 1.15, 0, 0.25, -0.25, -0.18, 1.0, 0],
  stride: [-0.95, 0, 0.08, 0.95, -0.1, 0.8, 0, -0.08, 0.6, 0.4],
  tuck: [-1.25, 0, 0.18, 1.75, 0.35, -0.6, 0, -0.18, 1.55, 0.35],
  spread: [-0.95, 0.1, 0.2, 0.6, 0.3, 0.55, -0.1, -0.2, 1.05, 0.3],
  trail: [-0.35, 0, 0.12, 0.95, 0.25, 0.45, 0, -0.12, 0.4, 0.3],
  kneel: [-2.0, 0.15, 0.14, 2.6, 0, 0.2, -0.1, -0.14, 1.45, -0.6],
  /** Off the floor: legs together, a little bent, toes pointed. */
  jump: [-0.15, 0, 0.06, 0.3, 0.55, 0.12, 0, -0.06, 0.4, 0.55],
  /** Stopped beyond a foe: the front knee bent deep, the back leg stretched out behind. */
  lunge: [-0.85, 0.25, 0.22, 1.0, 0, 0.62, -0.25, -0.22, 0.25, 0.15],
} satisfies Record<string, Legs>;
const between = (a: Legs, b: Legs, k: number) => a.map((v, i) => v + (b[i] - v) * k) as unknown as Legs;

/** Pose the legs; `planted` of the front foot (and of the back one, unless kneeling) flattens on the floor whatever the leg does. */
function strike(pose: Pose, l: readonly number[], planted: number, kneel = 0) {
  rot(pose, B.thighL, l[0], l[1], l[2]);
  rot(pose, B.shinL, l[3]);
  rot(pose, B.footL, l[4]);
  rot(pose, B.thighR, l[5], l[6], l[7]);
  rot(pose, B.shinR, l[8]);
  rot(pose, B.footR, l[9]);
  if (planted <= 0) return;
  const flatL = qMul(qConjugate(qMul(pose.rot[B.thighL], pose.rot[B.shinL])), qAxis([0, 1, 0], 0.25));
  pose.rot[B.footL] = qSlerp(pose.rot[B.footL], flatL, planted);
  const flatR = qMul(qConjugate(qMul(pose.rot[B.thighR], pose.rot[B.shinR])), qAxis([0, 1, 0], -0.25));
  pose.rot[B.footR] = qSlerp(pose.rot[B.footR], flatR, planted * (1 - kneel));
}
const qConjugate = (q: Quat): Quat => [-q[0], -q[1], -q[2], q[3]];

/** The free arm: upper arm about X, Y and Z and the elbow's bend, holding the scabbard, pumping, flung out or raised. */
type Arm = readonly [number, number, number, number];
const FREE = {
  sheath: [-0.6, 0.2, 0.62, -1.15],
  back: [0.55, 0, -0.85, -0.35],
  out: [-0.35, 0, -1.2, -0.45],
  run: [0.85, 0, -0.3, -0.6],
  up: [-2.6, 0, -0.35, -0.3],
  point: [-1.4, 0.3, -0.2, -0.15],
} satisfies Record<string, Arm>;
function freeArm(pose: Pose, a: readonly number[]) {
  rot(pose, B.upperArmR, a[0], a[1], a[2]);
  rot(pose, B.foreArmR, a[3]);
  rot(pose, B.handR, -0.1, 0, 0.1);
}

// ---------------------------------------------------------------------------
// the weapon
// ---------------------------------------------------------------------------

/** Roughly how long a hero's weapon arm is, to turn a hand's place into a reach. */
const ARM = 0.37;

/**
 * A place for the weapon hand and how the weapon is turned (its shaft along `blade`, rolled about itself), in the hero's
 * own frame (the hand's from the weapon shoulder), with where the elbow points and how far the fist is rolled about the
 * forearm to close round the weapon so turned (measured on the heroes' arms: a half turn holds it the other way up).
 */
interface Grip { hand: V3; aim: Quat; pole: V3; twist: number }
/** Elbows: out to the weapon side, down, back, and out, down and back. */
const OUT: V3 = [1, 0, 0], DOWN: V3 = [0, -1, 0], BACK: V3 = [0.2, -0.3, -1];
const OUT_BACK: V3 = [0.55, -0.6, -0.6];
/** Elbows square to a cut's swing, so the arm never swings through where its elbow points: a slash, the rising cut. */
const SLASHING: V3 = [0.39, -0.82, -0.38], RISING: V3 = [0.6, 0, -0.8];
const grip = (hand: V3, blade: V3, roll: number, pole: V3, twist: number): Grip => ({ hand, aim: aimAlong(blade, roll), pole: normalize(pole), twist });

/** The sword along its scabbard (held across the belly in the free hand), the hand on the hilt. */
const SHEATH_AXIS = normalize([-0.9, -0.2, -0.38]);
const SHEATHED = grip([-0.12, -0.2, 0.22], SHEATH_AXIS, 0, BACK, 4.23);
/** Drawn as far as the fist goes, the tip still at the scabbard's mouth. */
const AT_MOUTH = grip([0.13, -0.144, 0.326], SHEATH_AXIS, 0, DOWN, 4.55);
const GUARD = grip([0.12, -0.17, 0.26], [0.25, 0.5, 1], 0, OUT, 1.9);
/** Halfway out of the scabbard's mouth and across, the blade swung out level to the weapon side. */
const ACROSS = grip([0.28, -0.13, 0.22], [1, 0.05, 0.3], 0, OUT, 2.89);
/** Running in, the blade held back and low. */
const TRAILING = grip([0.17, -0.26, -0.04], [0.35, -0.4, -1], 0, BACK, 3.08);
const LOW = grip([0.17, -0.26, 0.06], [0.2, -0.85, 0.5], 0, RISING, 3.58);
/** Halfway up the rising cut, the blade swinging up ahead. */
const MID_RISE = grip([0.13, 0.0, 0.3], [0.15, 0.2, 1], 0, RISING, 2.6);
const HIGH = grip([0.07, 0.27, 0.12], [0.05, 1, -0.25], 0, RISING, 2.57);
/** Wound up over the weapon shoulder, and through a cut to low across the body. */
const WOUND = grip([0.19, 0.11, -0.04], [0.6, 0.45, -0.65], 0, SLASHING, 2.6);
/** Halfway through a slash, the blade leading down and ahead. */
const MID_CUT = grip([0.08, -0.06, 0.25], [0.4, -0.55, 0.75], 0, SLASHING, 3.18);
const THROUGH = grip([-0.11, -0.17, 0.25], [-0.35, -0.85, 0.4], -0.3, SLASHING, 3.43);
const OVERHEAD = grip([0.02, 0.29, 0.0], [0.1, 0.25, -1], 0, OUT, 1.81);
/** Halfway down a blow from overhead, the blade level ahead. */
const MID_CHOP = grip([0.03, 0.06, 0.3], [0.05, 0.15, 1], 0, OUT, 3.22);
const CHOPPED = grip([0.02, -0.21, 0.26], [0.05, -0.75, 0.65], 0, OUT, 3.5);
const PLUNGE = grip([0.06, -0.25, 0.2], [0.05, -1, 0.3], 0, OUT_BACK, 3.54);
/** Landed, the blade held out low to the side; then flicked clean. */
const OUT_LOW = grip([0.3, -0.12, 0.1], [0.85, -0.45, -0.2], 0, OUT_BACK, 3.02);
const FLICKED = grip([0.31, -0.08, 0.14], [0.9, -0.1, 0.4], 0, OUT_BACK, 2.78);
/** Flashing past, the blade swept out behind. */
const SWEPT = grip([0.3, -0.08, -0.06], [0.75, 0.1, -0.65], 0.4, OUT_BACK, 2.4);

/** Where a weapon other than a sword rests (it has no scabbard): stood up beside the hero, as in the stance. */
function stowed(ctx: ClipContext): Grip {
  if (!ctx.weapon) return grip([0.05, -0.3, 0.1], [0.2, -0.6, 1], 0, OUT, 0);
  if (ctx.weapon === 'sword') return SHEATHED;
  const [x, y, z] = HOLDS[ctx.weapon].aim;
  return grip([0.1, -0.27, 0.05], [x, y, z], 0, OUT, 0);
}

/** Put the weapon hand at a grip, turned with the hero's `frame`; a bare fist is left be. */
function hold(pose: Pose, frame: Quat, g: Grip, armed: boolean) {
  const reach = Math.hypot(g.hand[0], g.hand[1], g.hand[2]);
  pose.reach = qRotate(frame, normalize(g.hand));
  pose.reachExtent = clamp(reach / ARM, 0.4, 0.9);
  pose.reachPole = qRotate(frame, g.pole);
  pose.reachWeight = 1;
  if (!armed) return;
  pose.aim = qMul(frame, g.aim);
  pose.aimWeight = 1;
  pose.twist = g.twist;
}

/** Grips through keys, the hand swung round the shoulder rather than cut in past it, the weapon turned the shortest way. */
function trackGrip(u: number, keys: [number, Grip, Curve?][]): Grip {
  if (u <= keys[0][0]) return keys[0][1];
  for (let i = 0; i + 1 < keys.length; i++) {
    const [t0, a] = keys[i], [t1, b, curve = 'ease'] = keys[i + 1];
    if (u <= t1) return gripBetween(a, b, shape(curve, (u - t0) / (t1 - t0)));
  }
  return keys[keys.length - 1][1];
}

function gripBetween(a: Grip, b: Grip, k: number): Grip {
  const la = Math.hypot(...a.hand), lb = Math.hypot(...b.hand);
  const hand = qRotate(qSlerp([0, 0, 0, 1], qFromTo(a.hand, b.hand), k), normalize(a.hand)).map(v => v * mix(la, lb, k)) as V3;
  return {
    hand, aim: qSlerp(a.aim, b.aim, k), pole: normalize([mix(a.pole[0], b.pole[0], k), mix(a.pole[1], b.pole[1], k), mix(a.pole[2], b.pole[2], k)]),
    twist: mix(a.twist, b.twist, k),
  };
}

/** A cut from one grip to another over `from`..`to`, straight through the middle grip: slow to leave, fastest midway, braking hard. */
function cut(u: number, from: number, to: number, a: Grip, b: Grip, through: Grip): Grip {
  const k = blow(u, from, to, 0.5);
  return k < 0.5 ? gripBetween(a, through, k * 2) : gripBetween(through, b, k * 2 - 1);
}

// ---------------------------------------------------------------------------
// shared
// ---------------------------------------------------------------------------

/** A blow of a super attack in its story: where its slash and its light, dust and straw go, and what it does to the screen. */
interface Beat {
  /** When it lands (story time). */
  s: number;
  /** Where (the run's frame: ahead along the run, up, towards the viewer), given the dash's length; left out, on the dummy's middle. */
  at?: (dash: number) => V3;
  /** Which way its cut runs (the run's frame) and how big it is. */
  along: V3;
  size: number;
  linger?: number;
  width?: number;
  shake?: number;
  flash?: number;
  impact?: boolean;
  burst?: boolean;
  dust?: boolean;
  ring?: boolean;
  straw?: number;
  flare?: boolean;
}

/** A camera take: from when (story time), eased in from the take before over `blend` real seconds (a cut without). */
interface Take {
  from: number;
  blend?: number;
  /** The shot at story time `s`, `k` of the way through the take, in the run's frame. */
  shot: (s: number, k: number, scene: Scene) => Framing;
}
interface Framing { eye: V3; target: V3; fov: number; roll?: number }
/** Where the hero's hips and the dummy's middle are, in the run's frame, and the dash's length. */
interface Scene { hero: V3; dummy: V3; dash: number }

interface Super {
  clock: Clock;
  story: number;
  beats: Beat[];
  /** Each beat's real time into a time round. */
  times: number[];
  takes: Take[];
  /** When the camera hands the view back (story time). */
  release: [number, number];
  target: (s: number, dir: number, dash: number) => Target;
}

function define(story: number, stops: [number, number][], slows: [number, number, number][], beats: Beat[], takes: Take[], release: [number, number], target: Super['target']): Super {
  const c = clock(story, stops, slows);
  return { clock: c, story, beats, times: beats.map(b => realAt(c, b.s)), takes, release, target };
}

/** Which time round `t` is in (each runs the other way), and how far into it. */
function roundOf(t: number, length: number) {
  const n = Math.floor(t / length);
  return { n, r: t - n * length, dir: n % 2 === 0 ? 1 : -1 };
}

/** The run's frame to character space: ahead along the run is `dir`'s way along X. */
const placed = (v: V3, dir: number): V3 => [v[0] * dir, v[1], v[2]];

/** How hard the view shakes and flashes `r` into a round, from the blows landed before it, and whether it shows an impact frame. */
function aftermath(sup: Super, r: number) {
  let shake = 0, flash = 0, impact = 0, punch = 0;
  sup.beats.forEach((b, i) => {
    const age = r - sup.times[i];
    if (age < 0) return;
    if (b.shake) shake = Math.max(shake, b.shake * Math.exp(-age / 0.3));
    if (b.flash) {
      flash = Math.max(flash, b.flash * Math.exp(-age / 0.08));
      punch = Math.max(punch, b.flash * Math.exp(-age / 0.18));
    }
    if (b.impact && age < 0.075) impact = Math.max(impact, age < 0.04 ? 1 : 0.5);
  });
  return { shake, flash, impact, punch };
}

/** The camera at story time `s` (`r` real seconds into the round), in character space. */
function camera(sup: Super, s: number, r: number, dir: number, scene: Scene, shake: number, punch: number): Shot {
  const takes = sup.takes;
  let i = 0;
  while (i + 1 < takes.length && takes[i + 1].from <= s) i++;
  const frame = (j: number): Framing => {
    const take = takes[j], start = realAt(sup.clock, take.from), end = j + 1 < takes.length ? realAt(sup.clock, takes[j + 1].from) : sup.clock.length;
    return take.shot(s, clamp((r - start) / Math.max(1e-6, end - start), 0, 1), scene);
  };
  let f = frame(i);
  const blend = takes[i].blend ?? 0, into = r - realAt(sup.clock, takes[i].from);
  if (blend > 0 && i > 0 && into < blend) {
    const before = frame(i - 1), k = ease(into / blend);
    f = {
      eye: before.eye.map((v, j) => mix(v, f.eye[j], k)) as V3, target: before.target.map((v, j) => mix(v, f.target[j], k)) as V3,
      fov: mix(before.fov, f.fov, k), roll: mix(before.roll ?? 0, f.roll ?? 0, k),
    };
  }
  const [a, b] = sup.release;
  return {
    eye: placed(f.eye, dir), target: placed(f.target, dir), fov: ((f.fov * Math.PI) / 180) * (1 - 0.14 * punch), roll: (f.roll ?? 0) * dir,
    weight: 1 - ramp(s, a, b), shake,
  };
}

/** The blows a super attack lands over `from`..`to` (clip time), for a dash of `dash`. */
function hitsOf(sup: Super, from: number, to: number, dash: number): Hit[] {
  const out: Hit[] = [], length = sup.clock.length;
  for (let n = Math.floor(from / length); n * length <= to; n++) {
    const dir = n % 2 === 0 ? 1 : -1;
    sup.beats.forEach((b, i) => {
      const time = n * length + sup.times[i];
      if (time <= from || time > to) return;
      out.push({
        time, at: b.at ? placed(b.at(dash), dir) : sup.target(b.s, dir, dash).at, along: placed(b.along, dir), size: b.size,
        linger: b.linger ?? 0, width: b.width ?? 1, burst: !!b.burst, dust: !!b.dust, ring: !!b.ring, straw: b.straw ?? 0, flare: !!b.flare,
      });
    });
  }
  return out;
}

/** The dummy standing at its place, facing the way the hero comes from, or popping into or out of being. */
const standing = (dir: number, size = 1): Target => ({ at: [0, DUMMY_MIDDLE, 0], turn: qEuler(0, (-dir * Math.PI) / 2, 0), size, flail: 0, split: 0 });

/** Where a dash stops short of the dummy (the run's frame), striking distance away. */
const nearOf = (dash: number) => -Math.min(0.7, 0.8 * dash);

// ---------------------------------------------------------------------------
// starfall
// ---------------------------------------------------------------------------

/** How high the dummy is juggled (its middle), and the star's radius round it. */
const HANG = 4.0, STAR_R = 1.7;
/** The star's points round the dummy (the run's frame), the top first, then on round as the viewer sees a run to the right turn anticlockwise. */
const POINT = (k: number): [number, number] => [Math.cos(Math.PI / 2 + (k * 2 * Math.PI) / 5) * STAR_R, Math.sin(Math.PI / 2 + (k * 2 * Math.PI) / 5) * STAR_R];
/** The order the hero passes them in, along the star's lines and back to the top. */
const ROUTE = [0, 2, 4, 1, 3, 0];
/** The passes: when the first starts, how long each takes, and how often they come. */
const STAR_FROM = 1.6, PASS = 0.1, PASS_EVERY = 0.2;
const passStart = (i: number) => STAR_FROM + i * PASS_EVERY;
/** Each line's way as the hero's turn from upright (radians): each 144° on from the last, round and round. */
const passTurn = (i: number) => ((162 + 144 * i) * Math.PI) / 180;
const STAR_TO = passStart(4) + PASS;
/** The beats: the draw, the rising cut, the cut up through it, the blow from above, the dummy hitting the floor, the landing, the flick and the click. */
const SF = { draw: 0.34, launch: 0.91, through: 1.36, chop: 2.75, meteor: 2.84, dive: 2.93, slam: 3.19, land: 3.23, flick: 3.96, click: 4.88, story: 6.93 };

/** The hero's place among the star's points (the run's frame, from the dummy's middle), at `s`. */
function starAt(s: number): [number, number] {
  for (let i = 0; i < 5; i++) {
    const a = POINT(ROUTE[i]), b = POINT(ROUTE[i + 1]), from = passStart(i);
    if (s < from) {
      // Hanging at the point it reached, having shot a little past it and come back.
      if (i === 0) return a;
      const p = POINT(ROUTE[i - 1]), over = jolt(s, from - (PASS_EVERY - PASS), 0.03) * 0.09, l = Math.hypot(a[0] - p[0], a[1] - p[1]);
      return [a[0] + ((a[0] - p[0]) / l) * over, a[1] + ((a[1] - p[1]) / l) * over];
    }
    if (s < from + PASS) {
      const k = blow(s, from, from + PASS, 0.5);
      return [mix(a[0], b[0], k), mix(a[1], b[1], k)];
    }
  }
  const top = POINT(0), p = POINT(ROUTE[4]), over = jolt(s, STAR_TO, 0.03) * 0.09, l = Math.hypot(top[0] - p[0], top[1] - p[1]);
  return [top[0] + ((top[0] - p[0]) / l) * over, top[1] + ((top[1] - p[1]) / l) * over];
}

/** The dummy's tumble (radians about its own X) while juggled: spun up by the rising cut and over by each pass. */
function juggleTumble(s: number) {
  let spin = -1.6 * Math.PI * shape('arc', clamp((s - SF.launch) / 0.45, 0, 1)) - 0.9 * ramp(s, SF.through, SF.through + 0.14);
  for (let i = 0; i < 5; i++) spin -= 0.7 * ramp(s, passStart(i) + PASS / 2, passStart(i) + PASS / 2 + 0.08);
  return spin;
}
/** Lying on its back, its head away from where the hero lands: the tumble the blow from above turns it to. */
const LYING = (() => {
  const before = juggleTumble(SF.meteor) - 2.6;
  return Math.PI / 2 + 2 * Math.PI * Math.round((before - Math.PI / 2) / (2 * Math.PI));
})();

function starfallTarget(s: number, dir: number, dash: number): Target {
  if (s < SF.launch) return standing(dir);
  const yaw = qEuler(0, (-dir * Math.PI) / 2, 0);
  if (s >= 5.78) return standing(-dir, keyed(s, [[6.23, 0], [6.38, 1.15, 'snap'], [6.48, 1]]));
  // Each pass jolts it along its line.
  let jf = 0, jy = 0;
  for (let i = 0; i < 5; i++) {
    const a = POINT(ROUTE[i]), b = POINT(ROUTE[i + 1]), l = Math.hypot(b[0] - a[0], b[1] - a[1]), k = jolt(s, passStart(i) + PASS / 2, 0.05) * 0.12;
    jf += ((b[0] - a[0]) / l) * k;
    jy += ((b[1] - a[1]) / l) * k;
  }
  const bob = Math.sin(s * 8) * 0.03 * ramp(s, 1.5, 1.65);
  let f: number, y: number, tumble: number;
  if (s < SF.meteor) {
    f = jf;
    y = keyed(s, [[SF.launch, DUMMY_MIDDLE], [SF.through, HANG - 0.15, 'arc'], [1.5, HANG + 0.12, 'arc'], [1.65, HANG]]) + jy + bob;
    tumble = juggleTumble(s);
  } else if (s < SF.slam) {
    // Driven down from the star to the floor ahead, turning over to land on its back.
    const k = (s - SF.meteor) / (SF.slam - SF.meteor);
    f = mix(jf, 0.1 * dash, ease(k));
    y = mix(HANG + jy, 0.2, k * k);
    tumble = mix(juggleTumble(SF.meteor), LYING, shape('snap', k));
  } else {
    // A bounce, and it lies there.
    f = 0.1 * dash + 0.1 * shape('snap', clamp((s - SF.slam) / 0.38, 0, 1));
    y = keyed(s, [[SF.slam, 0.2], [3.31, 0.5, 'arc'], [3.45, 0.2, 'late'], [3.51, 0.26, 'arc'], [3.57, 0.2, 'late']]);
    tumble = LYING + 0.35 * Math.sin(clamp((s - SF.slam) / 0.38, 0, 1) * Math.PI);
  }
  const flail = Math.min(1, ramp(s, SF.launch, SF.launch + 0.04) * (1 - 0.75 * ramp(s, SF.slam, 3.53)) + jolt(s, SF.click, 0.1));
  return {
    at: [dir * f, y, 0], turn: qMul(yaw, qEuler(tumble, 0, 0)), size: keyed(s, [[5.68, 1], [5.78, 0, 'late']]), flail,
    split: keyed(s, [[SF.click, 0], [5.68, 1, 'linear']]),
  };
}

const STARFALL_LEGS: Keys<Legs> = [
  [0, LEGS.ready], [0.18, between(LEGS.ready, LEGS.crouch, 0.45)], [0.32, between(LEGS.ready, LEGS.crouch, 0.45)], [0.38, LEGS.ready, 'snap'], [0.56, LEGS.ready],
  [0.6, between(LEGS.ready, LEGS.crouch, 0.7), 'snap'], [0.64, LEGS.stride, 'snap'], [0.8, LEGS.stride], [0.86, LEGS.crouch, 'snap'], [0.92, LEGS.crouch],
  [0.96, LEGS.jump, 'snap'], [1.0, LEGS.jump], [1.04, LEGS.crouch, 'snap'], [1.1, LEGS.jump, 'snap'], [1.3, LEGS.tuck], [1.38, LEGS.jump, 'snap'],
  [1.52, LEGS.tuck],
  ...[0, 1, 2, 3, 4].flatMap((i): Keys<Legs> => [
    [passStart(i), LEGS.tuck], [passStart(i) + 0.03, LEGS.trail, 'snap'], [passStart(i) + PASS, LEGS.trail], [passStart(i) + PASS + 0.06, LEGS.tuck],
  ]),
  [2.7, LEGS.spread], [SF.meteor, LEGS.tuck, 'snap'], [3.05, LEGS.trail], [3.18, LEGS.spread], [SF.land, LEGS.kneel, 'snap'], [5.33, LEGS.kneel], [5.93, LEGS.ready],
];
const STARFALL_FREE: Keys<Arm> = [
  [0, FREE.sheath], [0.5, FREE.sheath], [0.56, FREE.point, 'snap'], [0.62, FREE.run, 'snap'], [0.8, FREE.run], [0.88, FREE.out, 'snap'], [1.04, FREE.out],
  [1.1, FREE.up, 'snap'], [1.45, FREE.up], [1.6, FREE.out, 'snap'], [2.5, FREE.out], [2.72, FREE.up], [SF.meteor, FREE.out, 'snap'], [3.03, FREE.back],
  [SF.land, FREE.back], [3.29, FREE.out, 'snap'], [3.63, FREE.back], [4.08, FREE.back], [4.38, FREE.sheath], [5.33, FREE.sheath],
];
/** Turned in the star's plane, from upright, through each line, and back upright to strike down. */
const STARFALL_TURN: Key[] = [
  [1.5, 0], [STAR_FROM, passTurn(0)],
  ...[0, 1, 2, 3, 4].flatMap((i): Key[] => [[passStart(i) + PASS, passTurn(i)], ...(i < 4 ? [[passStart(i + 1), passTurn(i + 1)] as Key] : [])]),
  [2.7, 4 * Math.PI],
];

/**
 * Starfall: a hand on the hilt, the sword drawn in a flash and pointed at the dummy, a dash at it, a rising cut that
 * throws it high, a corkscrew leap after it cutting up through it, five passes through it along the lines of a star
 * that hang in the air, a blow from above that drives it down, a dive after it to land beyond on one knee, and the
 * blade flicked clean and slid slowly home; on the click the dummy falls apart along its cut.
 */
const STARFALL: Super = define(SF.story,
  [[SF.draw, 0.07], [SF.launch, 0.13], [SF.through, 0.05], ...[0, 1, 2, 3, 4].map((i): [number, number] => [passStart(i) + PASS / 2, i === 4 ? 0.07 : 0.035]),
    [SF.meteor, 0.16], [SF.land, 0.18], [SF.click, 0.1]],
  [[2.4, 2.52, 0.3], [2.52, 2.72, 0.5], [4.43, 4.83, 0.45]],
  [
    { s: SF.draw, at: dash => [-dash + 0.32, 0.45, 0.18], along: [1, 0.15, 0.4], size: 0.55, flash: 0.25, shake: 0.15 },
    { s: SF.launch, along: [0.25, 1, 0.1], size: 1.25, flash: 0.55, impact: true, shake: 0.75, burst: true, straw: 0.7 },
    { s: SF.through, along: [0.1, 1, 0.2], size: 1.05, flash: 0.2, shake: 0.4, straw: 0.45 },
    ...[0, 1, 2, 3, 4].map((i): Beat => {
      const a = POINT(ROUTE[i]), b = POINT(ROUTE[i + 1]), l = Math.hypot(b[0] - a[0], b[1] - a[1]);
      return {
        s: passStart(i) + PASS / 2, at: () => [(a[0] + b[0]) / 2, HANG + (a[1] + b[1]) / 2, 0.05], along: [b[0] - a[0], b[1] - a[1], 0], size: (l / 0.84) * 1.08,
        width: 0.45, linger: 3, flash: i === 4 ? 0.3 : 0.12, shake: 0.3, straw: 0.35,
      };
    }),
    { s: SF.meteor, along: [0.2, -1, 0.1], size: 1.5, flash: 0.85, impact: true, shake: 1, burst: true, flare: true, straw: 0.9 },
    { s: SF.slam, at: dash => [0.1 * dash, 0.2, 0], along: [1, 0.1, 0], size: 0.6, dust: true, shake: 0.5, straw: 0.5 },
    { s: SF.land, at: dash => [dash, 0.12, 0], along: [1, 0.05, 0.2], size: 0.9, flash: 0.6, impact: true, shake: 1, dust: true, ring: true },
    { s: SF.flick, at: dash => [dash + 0.38, 0.38, 0.18], along: [1, -0.25, 0.35], size: 0.5 },
    { s: SF.click, along: [0.35, 1, 0.2], size: 1.6, flash: 0.6, impact: true, shake: 0.6, burst: true, straw: 1 },
  ],
  [
    // The draw, close on the hero, pushing in.
    {
      from: 0, shot: (_s, k, { hero }) => ({
        eye: [hero[0] + mix(1.3, 1.1, k), mix(1.05, 1.1, k), mix(2.05, 1.75, k)], target: [hero[0] + 0.15, 1.0, 0], fov: 30, roll: -0.05,
      }),
    },
    // The dash, low alongside it.
    { from: 0.6, shot: (_s, _k, { hero }) => ({ eye: [hero[0] + 0.7, 0.6, 3.6], target: [hero[0] + 0.9, 0.7, 0], fov: 36, roll: 0.04 }) },
    // The rising cut from low down beyond the dummy, looking up after it as it flies.
    {
      from: 0.84, shot: (s, _k, { dummy }) => ({
        eye: [1.5, 0.15, 2.3], target: [mix(-0.4, dummy[0], ramp(s, SF.launch, 1.1)), mix(0.9, dummy[1] + 0.25, ramp(s, SF.launch, 1.1)), 0], fov: 46, roll: 0.06,
      }),
    },
    // The leap: rising with it, flying round.
    {
      from: 1.04, blend: 0.15, shot: (_s, k, { hero, dummy }) => {
        const a = mix(0.55, -0.4, ease(k)), d = mix(4.4, 6.6, ease(k)), y = (hero[1] + dummy[1]) / 2;
        return { eye: [Math.sin(a) * d, y - 0.5, Math.cos(a) * d], target: [(hero[0] + dummy[0]) / 2, y + 0.15, 0], fov: 42 };
      },
    },
    // The star, face on, wheeling slowly round it; in close for the last line, slowed.
    {
      from: STAR_FROM, blend: 0.12, shot: (s, k, { hero }) => {
        // In on the last line, looking up after the hero as it comes round to the top.
        const a = mix(-0.3, 0.28, k), d = mix(7.4, 6.6, k), up = ramp(s, 2.38, STAR_TO);
        return { eye: [Math.sin(a) * d, HANG + 0.3, Math.cos(a) * d], target: [hero[0] * up, mix(HANG + 0.05, hero[1] - 0.2, up), 0], fov: 40 - 8 * up };
      },
    },
    // The blow from above, from below.
    { from: STAR_TO, shot: (_s, _k, { hero }) => ({ eye: [-1.3, HANG - 1.5, 3.0], target: [hero[0], hero[1] + 0.2, 0], fov: 40, roll: 0.12 }) },
    // The dive from the floor ahead, and the landing, drawing in on the hero down on one knee with the dummy behind.
    {
      from: SF.dive, shot: (s, _k, { hero, dash }) => {
        const w = ramp(s, SF.land, 3.63);
        return {
          eye: [dash + mix(1.5, 1.25, w), mix(0.25, 0.5, w), mix(2.4, 2.1, w)],
          target: [mix(hero[0], dash - 0.45, w), mix(hero[1] + 0.2, 0.6, w), 0], fov: mix(52, 40, w),
        };
      },
    },
    // The sheathing, close.
    { from: 3.83, shot: (_s, k, { dash }) => ({ eye: [dash + mix(1.55, 1.3, k), mix(0.95, 0.9, k), mix(2.6, 2.25, k)], target: [dash - 0.1, 0.7, 0], fov: 30, roll: -0.06 }) },
    // The click, wide: the hero knelt beyond, the dummy falling apart.
    { from: SF.click, shot: (_s, _k, { dash }) => ({ eye: [dash + 1.9, 1.25, 4.3], target: [0.5 * dash, 0.55, 0], fov: 34 }) },
  ],
  [5.63, 6.43],
  starfallTarget,
);
export const STARFALL_CYCLE = STARFALL.clock.length;

export function starfall(pose: Pose, t: number, ctx: ClipContext): ClipEvents {
  resetPose(pose);
  const sup = STARFALL, { n, r, dir } = roundOf(t, sup.clock.length), s = storyAt(sup.clock, r);
  const events = noEvents(), dash = ctx.dashDistance, sword = ctx.weapon === 'sword', side = (dir * Math.PI) / 2, rest = stowed(ctx), near = nearOf(dash);

  // The legs first: how high the hips stand on them is where a leap leaves the floor and where a landing comes down to.
  const kneel = keyed(s, [[3.21, 0], [SF.land, 1, 'snap'], [5.33, 1], [5.93, 0]]);
  const planted = keyed(s, [
    [0.6, 1], [0.64, 0, 'snap'], [0.8, 0], [0.86, 1, 'snap'], [0.92, 1], [0.96, 0, 'snap'], [1.0, 0], [1.04, 1, 'snap'], [1.08, 0, 'snap'], [3.21, 0],
    [SF.land, 1, 'snap'],
  ]);
  strike(pose, keyedAll(s, STARFALL_LEGS), planted, kneel);
  const stand = hipsHeight(pose, ctx);

  // Where the hero is (the run's frame): ahead along the run, and in the air its hips' height.
  let f: number, y: number, frame: Quat;
  if (s < 1.04) {
    f = keyed(s, [[0.6, -dash], [0.8, near, 'snap']]);
    pose.lift = keyed(s, [[0.9, 0], [0.96, 0.14, 'arc'], [1.04, 0, 'late']]);
    y = stand + pose.lift;
    const lean = keyed(s, [
      [0, 0], [0.18, 0.12], [0.34, -0.06, 'snap'], [0.56, 0], [0.6, 0.15, 'snap'], [0.64, 0.6, 'snap'], [0.8, 0.6], [0.86, 0.35, 'snap'], [0.96, -0.25, 'snap'],
      [1.04, 0.25],
    ]);
    frame = facing(side, lean);
  } else if (s < 1.5) {
    // Leaping after it in a corkscrew, to come round facing the viewer at the top.
    f = keyed(s, [[1.04, near], [SF.through, -0.75], [1.5, 0]]);
    y = keyed(s, [[1.04, stand], [SF.through, HANG - 0.35, 'arc'], [1.5, HANG + STAR_R, 'arc']]);
    pose.hover = keyed(s, [[1.04, 0], [1.1, 1]]);
    frame = facing(mix(side, dir * 4 * Math.PI, ease((s - 1.04) / 0.46)), keyed(s, [[1.04, 0.25], [1.12, -0.25, 'snap'], [1.4, -0.1], [1.5, 0]]));
  } else if (s < SF.dive) {
    // The star, then up and over to strike down.
    const [sf, sy] = starAt(Math.min(s, STAR_TO + 0.2));
    f = sf + keyed(s, [[SF.chop, 0], [SF.dive, 0.05]]);
    y = HANG + sy + keyed(s, [[STAR_TO, 0], [2.7, 0.18], [SF.chop, 0.2], [SF.dive, 0.42 - STAR_R, 'snap']]);
    pose.hover = 1;
    frame = inPlane(dir, keyed(s, STARFALL_TURN), keyed(s, [[STAR_TO, 0], [2.7, -0.4], [SF.chop, -0.45], [SF.meteor, 0.5, 'snap'], [SF.dive, 0.55]]));
  } else {
    // The dive, the landing, down on one knee; up again, and round to face where the next time round runs from.
    f = keyed(s, [[SF.dive, 0.05], [SF.land, dash]]);
    y = keyed(s, [[SF.dive, HANG + 0.42], [SF.land, stand, 'late']]);
    pose.hover = keyed(s, [[3.17, 1], [SF.land, 0]]);
    const yaw = keyed(s, [[SF.dive, 0], [3.03, side], [5.93, side], [6.33, 0], [6.73, -side]]);
    const lean = keyed(s, [[SF.dive, 0.55], [3.01, 0.8], [3.13, 0.8], [SF.land, 0.15], [3.27, 0.4, 'snap'], [3.53, 0.25], [5.33, 0.25], [5.93, 0]]);
    frame = facing(yaw, lean);
  }
  pose.pos[B.root] = [dir * f, 0, 0];
  pose.centre = [dir * f, y, 0];
  pose.rot[B.root] = frame;

  // The body: coiled to draw, bent into the dash, arched up the rising cut, hunched on landing and bowed over the knee.
  const impact = jolt(s, SF.land, 0.07);
  rot(pose, B.hips, 0.1 * kneel + 0.12 * impact, 0, 0);
  rot(pose, B.chest, keyed(s, [[0, -0.04], [0.18, 0.1], [0.34, -0.12, 'snap'], [0.56, -0.04], [0.64, 0.18, 'snap'], [0.86, 0.14], [0.96, -0.18, 'snap'], [1.1, 0], [3.18, 0], [3.27, 0.2, 'snap'], [3.73, 0.14], [5.33, 0.14], [5.93, -0.04]]) + 0.2 * impact,
    keyed(s, [[0, 0.1], [0.18, 0.4], [0.34, -0.25, 'snap'], [0.56, 0.05], [0.8, 0.05], [0.86, 0.3, 'snap'], [0.96, -0.3, 'snap'], [1.1, 0], [5.93, 0], [6.53, 0.1]]), 0);
  rot(pose, B.head, keyed(s, [[0, 0], [0.18, 0.18], [0.34, -0.08, 'snap'], [0.6, 0], [1.04, 0], [1.12, -0.3, 'snap'], [1.5, 0], [SF.land, 0], [3.53, 0.3], [4.73, 0.3], [SF.click, -0.12, 'snap'], [5.43, 0]]), 0, 0);
  freeArm(pose, keyedAll(s, STARFALL_FREE));

  // The weapon: drawn and pointed, carried back low, the rising cut, the cut up through it, five slashes back and forth,
  // overhead, the blow, plunged down the dive, held out on landing, flicked and slid home.
  let g: Grip;
  if (s < 0.56) g = sword ? trackGrip(s, [[0.04, SHEATHED], [0.16, AT_MOUTH], [0.43, ACROSS], [0.56, GUARD]]) : trackGrip(s, [[0.1, rest], [0.5, GUARD]]);
  else if (s < 0.84) g = trackGrip(s, [[0.56, GUARD], [0.7, TRAILING], [0.72, TRAILING], [0.84, LOW]]);
  else if (s < 1.0) g = cut(s, 0.84, 0.99, LOW, HIGH, MID_RISE);
  else if (s < 1.26) g = trackGrip(s, [[1.0, HIGH], [1.12, TRAILING, 'snap'], [1.24, LOW]]);
  else if (s < 1.45) g = cut(s, 1.26, 1.43, LOW, HIGH, MID_RISE);
  else if (s < STAR_FROM - 0.035) g = trackGrip(s, [[1.45, HIGH], [STAR_FROM - 0.035, WOUND]]);
  else if (s < STAR_TO + 0.035) {
    const i = Math.min(4, Math.max(0, Math.floor((s - STAR_FROM + 0.035) / PASS_EVERY))), forth = i % 2 === 0;
    g = cut(s, passStart(i) - 0.035, passStart(i) + PASS + 0.035, forth ? WOUND : THROUGH, forth ? THROUGH : WOUND, MID_CUT);
  } else if (s < SF.chop - 0.02) g = s < 2.65 ? cut(s, STAR_TO + 0.035, 2.65, THROUGH, WOUND, MID_CUT) : trackGrip(s, [[2.65, WOUND], [SF.chop - 0.02, OVERHEAD]]);
  else if (s < SF.dive + 0.02) g = cut(s, SF.chop - 0.02, SF.dive + 0.02, OVERHEAD, CHOPPED, MID_CHOP);
  else if (s < 4.08) {
    // A sword plunged down the dive; a longer shaft held out level, clear of the floor it comes down to.
    g = trackGrip(s, sword
      ? [[SF.dive + 0.02, CHOPPED], [3.06, PLUNGE], [3.14, PLUNGE], [3.3, OUT_LOW], [3.91, OUT_LOW], [SF.flick, FLICKED, 'snap'], [4.03, OUT_LOW]]
      : [[SF.dive + 0.02, CHOPPED], [3.12, FLICKED], [3.6, FLICKED], [3.91, OUT_LOW], [SF.flick, FLICKED, 'snap'], [4.03, OUT_LOW]]);
  }
  else if (sword) g = trackGrip(s, [[4.08, OUT_LOW], [4.43, AT_MOUTH], [SF.click, SHEATHED, 'late']]);
  else g = trackGrip(s, [[4.08, OUT_LOW], [4.73, rest]]);
  // A weapon without a scabbard is twirled up as it is drawn.
  if (!sword && s < 0.6) g = { ...g, aim: qMul(g.aim, qAxis([0, 1, 0], Math.PI * 2 * ease((s - 0.1) / 0.4))) };
  hold(pose, frame, g, ctx.weapon !== null);
  // How much of the blade is in the scabbard: out in a flash, slid slowly home, the last of it quick.
  if (sword) events.sheath = keyed(s, [[0.04, 1], [0.1, 0.85], [0.16, 0, 'snap'], [4.43, 0], [4.78, 0.72], [SF.click, 1, 'late']]);

  const pass = [0, 1, 2, 3, 4].some(i => during(s, passStart(i) - 0.01, passStart(i) + PASS + 0.02));
  events.ghost = during(s, 0.62, 0.82) || during(s, 1.06, 1.48) || pass || during(s, SF.chop, SF.dive) || during(s, 2.95, 3.21);
  events.trail = during(s, 0.88, 0.97) || during(s, 1.3, 1.43) || during(s, 2.77, 2.92) || during(s, 3.93, 4.0) ? 1 : 0;
  events.dust = during(s, 0.6, 0.64) || during(s, 1.04, 1.08) || during(s, SF.land, 3.29) ? 1 : during(s, 0.8, 0.86) ? 0.8 : during(s, 0.9, 0.94) ? 0.6 : 0;
  events.target = starfallTarget(s, dir, dash);
  const after = aftermath(sup, r), dummy = placed(events.target.at, dir);
  events.camera = camera(sup, s, r, dir, { hero: [f, y, 0], dummy, dash }, after.shake, after.punch);
  events.screen = {
    flash: after.flash, impact: after.impact, mono: 0,
    speed: Math.max(during(s, 0.61, 0.8) ? 1 : 0, during(s, 1.06, 1.42) ? 0.8 : 0, pass ? 0.6 : 0, during(s, 2.95, 3.21) ? 1 : 0),
    // Dusk falls round the star while it is cut, and lifts as the hero comes down.
    dim: keyed(s, [[1.3, 0], [STAR_FROM, 1], [SF.dive, 1], [SF.land, 0.25, 'snap'], [3.6, 0]]),
  };
  secondary(pose, n * sup.story + s, ctx, 0.8);
  return events;
}

// ---------------------------------------------------------------------------
// thousand cuts
// ---------------------------------------------------------------------------

/** The beats: the glint, the flash step through the dummy, the flick, the cuts showing and the click. */
const TC = { glint: 0.42, step: 0.45, through: 0.49, flick: 0.8, cuts: 0.8, click: 1.85, story: 3.7 };
const CUT_COUNT = 28;
/** When each cut shows (story time): slowly at first, then faster and faster. */
const cutAt = (k: number) => TC.cuts + 0.98 * (k / (CUT_COUNT - 1)) ** 0.6;
const TC_STOPS: [number, number][] = [[TC.glint, 0.08], [TC.through, 0.2], [TC.click, 0.14]];
const TC_SLOWS: [number, number, number][] = [[1.45, 1.8, 0.4]];
const TC_CLOCK = clock(TC.story, TC_STOPS, TC_SLOWS);

function cutsTarget(s: number, dir: number): Target {
  if (s >= 3.1) return standing(-dir, keyed(s, [[3.2, 0], [3.35, 1.15, 'snap'], [3.45, 1]]));
  // A shudder with each cut that shows; flung apart on the click.
  let shake = jolt(s, TC.through, 0.05) * 0.3;
  for (let k = 0; k < CUT_COUNT; k++) shake += jolt(s, cutAt(k), 0.035) * 0.07 * (k % 2 ? 1 : -1);
  const base = standing(dir);
  return {
    ...base, turn: qMul(base.turn, qEuler(0.4 * jolt(s, TC.through, 0.06), 0, shake)), flail: Math.min(1, Math.abs(shake) * 5 + jolt(s, TC.click, 0.12)),
    size: keyed(s, [[2.65, 1], [2.75, 0, 'late']]), split: keyed(s, [[TC.click, 0], [2.65, 1, 'linear']]),
  };
}

/**
 * Thousand cuts: crouched with a hand on the hilt, gathering itself, the hero's eyes glint and it flashes through the
 * dummy in a blink, drawing as it passes, and stops beyond with its back to it. The world holds its breath, drained of
 * colour, while the cuts it made show one after another all over the dummy, faster and faster, and the hero flicks
 * the blade and slides it slowly home; on the click they all flare at once and the dummy falls apart.
 */
const THOUSAND_CUTS: Super = define(TC.story, TC_STOPS, TC_SLOWS,
  [
    { s: TC.glint, at: dash => [-dash + 0.12, 0.8, 0.2], along: [1, 0.35, 0.5], size: 0.35, flash: 0.25, shake: 0.1 },
    { s: TC.through, along: [1, 0.04, 0], size: 3.1, width: 0.35, linger: realAt(TC_CLOCK, TC.click) - realAt(TC_CLOCK, TC.through) + 0.3, flash: 0.45, impact: true, shake: 0.55, straw: 0.5 },
    { s: TC.flick, at: dash => [dash + 0.38, 0.4, 0.18], along: [1, -0.25, 0.35], size: 0.5 },
    ...Array.from({ length: CUT_COUNT }, (_, k): Beat => {
      const a = hash3(k, 3, 7) * Math.PI * 2, tilt = (hash3(k, 5, 1) - 0.5) * 1.4;
      const at: V3 = [(hash3(k, 2, 9) - 0.5) * 0.3, DUMMY_MIDDLE + (hash3(k, 8, 4) - 0.4) * 0.55, 0.12 * (hash3(k, 6, 6) - 0.5)];
      return {
        s: cutAt(k), at: () => at, along: [Math.cos(a), Math.sin(a), tilt * 0.4], size: 0.9 + 0.6 * hash3(k, 9, 2), width: 0.6,
        linger: realAt(TC_CLOCK, TC.click) - realAt(TC_CLOCK, cutAt(k)) + 0.3, shake: 0.1, straw: 0.15,
      };
    }),
    { s: TC.click, along: [0.3, 1, 0.2], size: 1.8, flash: 1, impact: true, shake: 1, burst: true, flare: true, straw: 1 },
  ],
  [
    // Close on the eyes, gathering.
    {
      from: 0, shot: (_s, k, { dash }) => ({
        eye: [-dash + mix(0.8, 0.7, k), 1.2, mix(1.5, 1.3, k)], target: [-dash + 0.05, 1.1, 0], fov: 26, roll: 0.05,
      }),
    },
    // The flash step, wide from the side.
    { from: TC.step, shot: (_s, k) => ({ eye: [0, 0.8, mix(4.8, 4.4, k)], target: [0.15, 0.7, 0], fov: mix(42, 38, k) }) },
    // Round the dummy as the cuts show.
    {
      from: TC.cuts, blend: 0.25, shot: (_s, k) => {
        const a = mix(0.55, -0.95, ease(k));
        return { eye: [Math.sin(a) * 3.0, 1.0, Math.cos(a) * 3.0], target: [0, 0.6, 0], fov: 36 };
      },
    },
    // The sheathing, close and from in front, the dummy and its cuts behind.
    { from: 1.45, shot: (_s, k, { dash }) => ({ eye: [dash + mix(1.2, 1.0, k), 0.95, mix(1.7, 1.45, k)], target: [dash - 0.25, 0.75, 0], fov: 30, roll: -0.05 }) },
    // The click, wide: the hero beyond, the dummy falling apart.
    { from: TC.click, shot: (_s, _k, { dash }) => ({ eye: [dash + 1.4, 1.0, 3.0], target: [0.35 * dash, 0.6, 0], fov: 34 }) },
  ],
  [2.7, 3.4],
  cutsTarget,
);
export const CUTS_CYCLE = THOUSAND_CUTS.clock.length;

const CUTS_LEGS: Keys<Legs> = [
  [0, LEGS.ready], [0.3, LEGS.crouch], [0.44, LEGS.crouch], [0.47, LEGS.stride, 'snap'], [0.56, LEGS.stride], [0.66, LEGS.lunge], [2.6, LEGS.lunge], [3.1, LEGS.ready],
];

export function thousandCuts(pose: Pose, t: number, ctx: ClipContext): ClipEvents {
  resetPose(pose);
  const sup = THOUSAND_CUTS, { n, r, dir } = roundOf(t, sup.clock.length), s = storyAt(sup.clock, r);
  const events = noEvents(), dash = ctx.dashDistance, sword = ctx.weapon === 'sword', side = (dir * Math.PI) / 2, rest = stowed(ctx);
  strike(pose, keyedAll(s, CUTS_LEGS), keyed(s, [[0.44, 1], [0.47, 0, 'snap'], [0.6, 0], [0.66, 1, 'snap']]));
  // Gathered before the dummy; through it in a blink, skidding to a stop beyond.
  const f = keyed(s, [[TC.step, -dash], [0.53, dash - 0.15, 'snap'], [0.66, dash, 'snap']]);
  pose.pos[B.root] = [dir * f, 0, 0];
  const lean = keyed(s, [[0, 0], [0.3, 0.35], [TC.glint, 0.42], [0.47, 0.75, 'snap'], [0.56, 0.6], [0.7, 0.2], [2.6, 0.2], [3.1, 0]]);
  const frame = facing(keyed(s, [[0, side], [3.0, side], [3.3, 0], [3.6, -side]]), lean);
  pose.rot[B.root] = frame;
  rot(pose, B.chest, keyed(s, [[0, -0.04], [0.3, 0.14], [0.47, 0.1, 'snap'], [0.7, 0.05], [3.1, -0.04]]), keyed(s, [[0, 0.1], [0.3, 0.35], [0.47, -0.3, 'snap'], [0.7, 0], [3.1, 0.1]]), 0);
  // Eyes on the dummy, then down while the cuts show behind; up on the click.
  rot(pose, B.head, keyed(s, [[0, 0.1], [0.4, 0.04], [0.47, -0.1, 'snap'], [0.85, 0.22], [TC.click, 0.22], [1.9, -0.1, 'snap'], [2.6, 0], [3.6, 0.1]]), 0, 0);
  freeArm(pose, keyedAll(s, [[0, FREE.sheath], [0.45, FREE.sheath], [0.52, FREE.back, 'snap'], [1.1, FREE.back], [1.35, FREE.sheath], [3.7, FREE.sheath]] as Keys<Arm>));

  let g: Grip;
  if (sword) g = trackGrip(s, [[0.43, SHEATHED], [0.46, AT_MOUTH, 'linear'], [0.52, ACROSS, 'linear'], [0.6, SWEPT], [0.72, SWEPT], [TC.flick, FLICKED], [0.87, OUT_LOW], [0.95, OUT_LOW], [1.35, AT_MOUTH], [TC.click, SHEATHED, 'late']]);
  else g = trackGrip(s, [[0.3, rest], [0.5, ACROSS], [0.6, SWEPT], [0.72, SWEPT], [TC.flick, FLICKED], [0.87, OUT_LOW], [0.95, OUT_LOW], [1.6, rest]]);
  hold(pose, frame, g, ctx.weapon !== null);
  if (sword) events.sheath = keyed(s, [[0, 1], [0.35, 0.94], [0.43, 0.94], [0.47, 0, 'snap'], [1.35, 0], [1.75, 0.7], [TC.click, 1, 'late']]);

  events.charge = keyed(s, [[0.05, 0], [TC.glint, 1], [0.48, 1], [0.6, 0]]);
  events.ghost = during(s, TC.step, 0.56);
  events.trail = during(s, 0.47, 0.56) || during(s, 0.77, 0.83) ? 1 : 0;
  events.dust = during(s, TC.step, 0.49) ? 1 : during(s, 0.6, 0.68) ? 0.8 : 0;
  events.target = cutsTarget(s, dir);
  const after = aftermath(sup, r);
  events.camera = camera(sup, s, r, dir, { hero: [f, 0.4, 0], dummy: placed(events.target.at, dir), dash }, after.shake, after.punch);
  events.screen = {
    flash: after.flash, impact: after.impact, mono: keyed(s, [[0.55, 0], [0.8, 0.85], [TC.click, 0.85], [1.92, 0, 'snap']]), speed: during(s, TC.step, 0.56) ? 1 : 0,
    dim: keyed(s, [[0.55, 0], [0.8, 0.5], [TC.click, 0.5], [1.92, 0, 'snap']]),
  };
  secondary(pose, n * sup.story + s, ctx, 0.6);
  return events;
}

// ---------------------------------------------------------------------------
// the stage's questions
// ---------------------------------------------------------------------------

const SUPERS: Record<string, Super> = { starfall: STARFALL, thousandCuts: THOUSAND_CUTS };

/** Whether a clip is a super attack, with a dummy to strike and a camera of its own. */
export function isSuper(clip: string) {
  return clip in SUPERS;
}

/**
 * The blows a super attack lands after `from` and up to `to` (clip time), each where it lands (character space), for a
 * dash `dash` long; none for another clip.
 */
export function hitsBetween(clip: string, from: number, to: number, dash = 1.4): Hit[] {
  const sup = SUPERS[clip];
  return sup && to > from ? hitsOf(sup, from, to, dash) : [];
}

/** The clip time, in its first time round, at which a super attack's story reaches `story` seconds (for checks and captures). */
export function superTime(clip: string, story: number): number {
  const sup = SUPERS[clip];
  return sup ? realAt(sup.clock, story) : story;
}

/** How far into its story a super attack is at clip time `t` (for checks and captures). */
export function superStory(clip: string, t: number): number {
  const sup = SUPERS[clip];
  return sup ? storyAt(sup.clock, roundOf(t, sup.clock.length).r) : t;
}
